


import { Component, Vue, Watch } from "vue-property-decorator";

import { CustomError, CustomValue, SelectionFilter } from "@Internal/Models";
import { Filter } from "@Internal/Contracts";

import { AdsPublications, Filters, Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import { NewsletterOffer } from "App/Models/Newsletter";

import PopupWindow from "Web/Components/Popup/Window.vue";
import RecordsComponent from "Web/Components/Records.vue";

import { ErrorMessage, FormatDate } from "./Helpers";

type OfferFilter = "enabled" | "disabled" | "all";

function isNotFound( error: CustomError ): boolean {
    return Number( error.Id ) === 404 || String( error.Id ) === "NotFound" ||
        /not found/i.test( String( error.Message ) );
}

/* Un client tel que le rend la liste des clients des portails ( `AdsPublications.
   GetAllCustomers` ) : la seule lecture de la Console qui porte le numero de groupe. */
interface GroupCustomer {
    Company: string;
    Owner: string;
    Email: string;
    Group: number;
}

/* LA NEWSLETTER AUTOMATIQUE — LES CLIENTS DE L'OFFRE ( Fred, 21/10/2026 : « je vois
   pas comment je peux voir les societes activees ou pas » ).

   L'ecran s'ouvre sur le tableau des clients qui ont l'offre, activee ou non
   ( `console/features/newsletter`, demandee au back le 21/10 ) : leurs newsletters,
   combien sont allumees, le prochain envoi, qui a active et quand. Un clic ouvre
   la page du client. « Activer pour un client » cherche un client qui ne l'a pas
   encore.

   Tant que la route n'existe pas ( 404 ), le tableau le dit et la recherche reste
   affichee : on peut toujours ouvrir un client. */
@Component({
    components: {
        PopupWindow,
        Records: RecordsComponent
    }
})
export default class NewslettersIndex extends Vue {
    private offers: NewsletterOffer[] | null = null;
    private pending: boolean = false;

    /* Ce qui manque quand la liste ne vient pas : le relais du back de la Console
       ( pas encore deploye ) ou la route de l'API ( demandee le 21/10 ). */
    private missing: "relay" | "route" | "" = "";
    private loading: boolean = true;
    private filter: OfferFilter = "enabled";

    private picking: boolean = false;
    private keyword: string = "";
    private timer: number = 0;
    private searching: boolean = false;
    private searched: boolean = false;
    private customers: GroupCustomer[] = [];

    /* Tous les clients, lus une fois a la premiere recherche. */
    private allCustomers: GroupCustomer[] | null = null;

    private readonly filters: OfferFilter[] = [ "enabled", "disabled", "all" ];

    /* AU DS DES LISTES DE LA CONSOLE ( Fred, 26/10/2026 : « check design DS global » ) :
       le meme `Records` que les Cles API — titre et nombre, action en tete, tableau en
       carte pleine largeur, statut en couleur. La liste est entiere ( la route ne pagine
       pas ) : le filtre se fait ici. */
    private columns: Column[] = [
        Column.Static( "Customer", $t( "NEWSLETTERS_colCustomer" ) ),
        Column.Static( "Offer", $t( "NEWSLETTERS_colOffer" ) ),
        Column.Static( "Newsletters", $t( "NEWSLETTERS_colNewsletters" ) ),
        Column.Static( "Active", $t( "NEWSLETTERS_colActive" ) ),
        Column.Static( "NextSend", $t( "NEWSLETTERS_nextSend" ) ),
        Column.Static( "EnabledBy", $t( "NEWSLETTERS_colEnabledBy" ) )
    ];

    private pagerSorter: PagerSorter = new PagerSorter( 9999 );

    private rows: ( NewsletterOffer & { Id: number } )[] = [];

    /* L'identifiant des filtres de la liste, pose apres « Activee » ( `assignDefaultFilter` ). */
    /* ⚠️ Chaine vide et non `undefined` : un champ `undefined` n'est pas reactif. */
    private filtersId: string = "";

    async mounted(): Promise<void> {
        await this.load();
    }

    private async load(): Promise<void> {
        this.loading = true;

        const [ offers, error ] = await Newsletters.Offers();

        this.loading = false;

        if( error !== undefined && isNotFound( error ) ) {
            /* Le back de la Console repond « Page not found » sur un service qu'il ne
               connait pas ; le relais, lui, rend le 404 de l'API. */
            this.missing = ( String( error.Message ) === "Page not found" ) ? "relay" : "route";
            this.pending = true;
            this.offers = [];
            return;
        }

        if( error !== undefined ) {
            EventBus.Publish(
                "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
            );
            return;
        }

        this.pending = false;
        this.missing = "";
        this.offers = offers || [];
        await this.assignDefaultFilter();
    }

    private get pendingText(): string {
        return this.$t(
            this.missing === "relay" ? "NEWSLETTERS_relayMissing" : "NEWSLETTERS_offersPending"
        ).toString();
    }

    private get shown(): NewsletterOffer[] {
        const offers = this.offers || [];

        if( this.filter === "all" )
            return offers;

        return offers.filter( offer => offer.enabled === ( this.filter === "enabled" ) );
    }

    private count( filter: OfferFilter ): number {
        const offers = this.offers || [];

        if( filter === "all" )
            return offers.length;

        return offers.filter( offer => offer.enabled === ( filter === "enabled" ) ).length;
    }

    private name( offer: NewsletterOffer ): string {
        return offer.company || offer.group_name || `#${ offer.group_id }`;
    }

    private state( offer: NewsletterOffer ): string {
        return this.$t( offer.enabled ? "NEWSLETTERS_stateOn" : "NEWSLETTERS_stateOff" ).toString();
    }

    private date( value: string | null ): string {
        return FormatDate( value );
    }

    private author( value: string | null ): string {
        return ( value || "" ).replace( /^console:/, "" );
    }

    /* ⚠️ Une copie a chaque mise a jour, pas un calcul : `Records` se met en
       chargement a chaque `Update` et n'en sort que quand `Rows` CHANGE. */
    private update( filters: Filter[] = [] ): void {
        const [ offer ] = filters.filter( filter => filter.Id === "Offer" );
        const values = ( offer === undefined ) ? [] :
            ( offer.Value as any[] ).map( value => String( value.Value ) );

        this.filter = ( values.length === 1 ) ? values[ 0 ] as OfferFilter : "all";

        const updated = this.pagerSorter.Copy();

        updated.Total = this.shown.length;
        this.pagerSorter = updated;
        this.rows = this.shown.map( offer => ( { ...offer, Id: offer.group_id } ) );
    }

    /* LE FILTRE DE LA CONSOLE ( Fred, 26/10/2026 : « plutot un filtre » ) : le meme que
       celui des Abonnements — une puce « Offre : Activee », « Ajouter un filtre », la croix
       pour le retirer. Les choix portent le nombre de clients. Pose a « Activee » a
       l'ouverture. */
    private getFilters(): Filter[] {
        return [
            SelectionFilter.Multi(
                "Offer",
                $t( "NEWSLETTERS_colOffer" ),
                this.offerValues(),
                $t( "NEWSLETTERS_colOffer" )
            )
        ];
    }

    private offerValues(): CustomValue<string>[] {
        return [ "enabled", "disabled" ].map( item => new CustomValue<string>(
            `${ $t( `NEWSLETTERS_filter_${ item }` ) } (${ this.count( item as OfferFilter ) })`,
            item
        ) );
    }

    private async assignDefaultFilter(): Promise<void> {
        const [ enabled ] = this.offerValues();

        await Filters.Assign( "NewsletterOffers", this.getFilters(), "Offer", [ enabled ], true );

        this.filtersId = "NewsletterOffers";
    }

    private rowAction( id: string, rows: NewsletterOffer[] ): void {
        if( id === "open" && rows.length > 0 )
            this.openOffer( rows[ 0 ] );
    }

    private openOffer( offer: NewsletterOffer ): void {
        this.$router.push( {
            name: "newsletters-group",
            params: { groupId: String( offer.group_id ) },
            query: {
                company: offer.company || offer.group_name || "",
                email: offer.owner_email || ""
            }
        } );
    }

    @Watch( "keyword" )
    private onKeyword(): void {
        window.clearTimeout( this.timer );

        if( this.keyword.trim().length < 2 ) {
            this.customers = [];
            this.searched = false;
            return;
        }

        this.timer = window.setTimeout( () => this.search(), 300 );
    }

    private async search(): Promise<void> {
        const keyword = this.keyword.trim().toLowerCase();

        if( this.allCustomers === null ) {
            this.searching = true;

            const [ rows, , error ] = await AdsPublications.GetAllCustomers(
                [], new PagerSorter( 9999 )
            );

            this.searching = false;

            if( error !== undefined ) {
                EventBus.Publish( "Error", error );
                return;
            }

            this.allCustomers = ( rows as any[] )
                .map( ( row: any ): GroupCustomer => ( {
                    Company: String( row.Company || "" ),
                    Owner: String( row.Owner || "" ),
                    Email: String( row.Email || "" ),
                    Group: Number( row.Group ) || 0
                } ) )
                .filter( customer => customer.Group > 0 );
        }

        if( keyword !== this.keyword.trim().toLowerCase() )
            return;

        this.searched = true;
        this.customers = this.allCustomers
            .filter( customer => this.matches( customer, keyword ) )
            .slice( 0, 12 );
    }

    /* Societe, proprietaire, e-mail ou numero de groupe. */
    private matches( customer: GroupCustomer, keyword: string ): boolean {
        return [ customer.Company, customer.Owner, customer.Email, String( customer.Group ) ]
            .some( part => part.toLowerCase().indexOf( keyword ) >= 0 );
    }

    private describe( customer: GroupCustomer ): string {
        return [ customer.Owner, customer.Email, `#${ customer.Group }` ]
            .filter( part => part !== "" ).join( " — " );
    }

    private open( customer: GroupCustomer ): void {
        this.$router.push( {
            name: "newsletters-group",
            params: { groupId: String( customer.Group ) },
            query: {
                company: customer.Company || customer.Owner,
                name: customer.Company !== "" ? customer.Owner : "",
                email: customer.Email
            }
        } );
    }
}
